import { Suspense, useState } from 'react';
import { Context as ResponsiveContext } from 'react-responsive';
import { hydrate, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Provider as ReakitProvider } from 'reakit';

import { I18nProvider } from '@cx-modules/i18n-provider';
import { DefaultLoadingFallback } from '@cx-modules/react-utils';

import { StoreProvider } from './providers/StoreProvider';
import { OddsGrid } from './OddsGrid';
import { type OddsGridIndexProps } from './types';
import { breakpoints } from './utils';

export type { OddsGridIndexProps, OverrideBookie } from './types';

declare global {
  interface Window {
    bettingActions: {
      addOrRemoveFromBetslip: (
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        betArray: any[],
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        matchBetIds?: any[],
        bookie?: string,
        betOdds?: string,
        betName?: string,
        betLocation?: string,
        subeventName?: string,
        marketName?: string,
        eventName?: string,
      ) => void;
    };
  }
}

const createQueryClient = (nocache?: boolean) =>
  new QueryClient({
    defaultOptions: {
      queries: {
        gcTime: nocache ? 0 : undefined,
        refetchOnMount: nocache ? true : false,
        staleTime: nocache ? 0 : Infinity,
      },
    },
  });

type OddsGridContentProps = Omit<OddsGridIndexProps, 'dehydratedState' | 'deviceType' | 'nocache'>;

const OddsGridContent = (props: OddsGridContentProps) => (
  <Suspense fallback={<DefaultLoadingFallback />}>
    <StoreProvider {...props}>
      <I18nProvider i18nInstance={props.i18n}>
        <OddsGrid {...props} />
      </I18nProvider>
    </StoreProvider>
  </Suspense>
);

function OddsGridWithQuery({ dehydratedState, deviceType, nocache, ...props }: OddsGridIndexProps) {
  const [queryClient] = useState(() => {
    const client = createQueryClient(nocache);
    hydrate(client, dehydratedState);
    return client;
  });

  const content = (
    <ReakitProvider>
      <QueryClientProvider client={queryClient}>
        <OddsGridContent {...props} />
      </QueryClientProvider>
    </ReakitProvider>
  );

  if (typeof window !== 'undefined') {
    if (props.enforceMobile) {
      return <ResponsiveContext.Provider value={{ width: breakpoints.phoneSm }}>{content}</ResponsiveContext.Provider>;
    }
    return content;
  }

  const deviceWidth =
    deviceType === 'MOBILE' ? breakpoints.phoneSm : deviceType === 'TABLET' ? breakpoints.tablet : breakpoints.desktop;

  return <ResponsiveContext.Provider value={{ width: deviceWidth }}>{content}</ResponsiveContext.Provider>;
}

export { OddsGridWithQuery };
